import { esquemaCargarFormato, FRECUENCIA_DIARIA, type Frecuencia } from "@gestiondoc/shared";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useRouteContext } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import { Dropzone } from "../componentes/Dropzone.tsx";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { SelectorFrecuencia } from "../componentes/Frecuencia.tsx";
import {
  Aviso,
  Boton,
  BotonEnlace,
  Campo,
  CLASE_ENTRADA,
  EstadoVacio,
  Tarjeta,
} from "../componentes/ui.tsx";
import { leerComoBase64, mensajeDe, mimeDeArchivo } from "../utiles.ts";

/**
 * Fase 3b · "Cargar formato existente": un administrador sube el PDF (o el Word) de un formato que ya
 * está aprobado en papel, con su código, revisión y fecha de aprobación, y queda vigente de una
 * vez, sin las tres firmas de la app, y listo para llenarse escribiendo encima de sus páginas.
 */

type Errores = Partial<Record<"codigo" | "nombre" | "revision" | "aprobadaEl" | "archivo", string>>;

export function PaginaCargarFormato() {
  const { perfil } = useRouteContext({ from: "/app" });
  const trpc = useTRPC();
  const navigate = useNavigate();
  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [revision, setRevision] = useState("1");
  const [aprobadaEl, setAprobadaEl] = useState("");
  const [retencion, setRetencion] = useState("24");
  const [loLlenoYo, setLoLlenoYo] = useState(true);
  const [frecuencia, setFrecuencia] = useState<Frecuencia>(FRECUENCIA_DIARIA);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [errores, setErrores] = useState<Errores>({});
  const [aviso, setAviso] = useState<string | null>(null);
  const [leyendo, setLeyendo] = useState(false);

  const cargar = useMutation(
    trpc.documentos.cargarFormato.mutationOptions({
      onSuccess: (r) => {
        queryClient.invalidateQueries();
        navigate({ to: "/documentos/$codigo/hojas", params: { codigo: r.codigo } });
      },
      onError: (e) => setAviso(mensajeDe(e)),
    }),
  );

  if (perfil.usuario.rolGlobal !== "administrador") {
    return (
      <EstadoVacio>
        Solo un administrador carga formatos que ya están aprobados en papel.
      </EstadoVacio>
    );
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setAviso(null);
    const mime = archivo ? mimeDeArchivo(archivo) : null;
    const nuevos: Errores = {};
    if (!archivo) nuevos.archivo = "Sube el PDF o el Word del formato";
    else if (!mime) nuevos.archivo = "Solo se aceptan PDF, Word o Excel";
    setLeyendo(true);
    const base64 = archivo && mime ? await leerComoBase64(archivo) : "";
    setLeyendo(false);
    const datos = esquemaCargarFormato.safeParse({
      codigo,
      nombre,
      revision: Number(revision),
      aprobadaEl,
      retencionMeses: Number(retencion),
      loLlenoYo,
      frecuencia,
      archivo: { nombre: archivo?.name ?? "", mime: mime ?? "application/pdf", base64 },
    });
    if (!datos.success) {
      for (const p of datos.error.issues) {
        const campo = p.path[0] as keyof Errores;
        if (campo && !nuevos[campo]) nuevos[campo] = p.message;
      }
      if (!Object.keys(nuevos).length)
        setAviso(datos.error.issues[0]?.message ?? "Revisa los datos");
    }
    setErrores(nuevos);
    if (Object.keys(nuevos).length || !datos.success) return;
    cargar.mutate(datos.data);
  }

  const ocupado = leyendo || cargar.isPending;
  return (
    <div className="max-w-[760px]">
      <Encabezado
        overline="Del día › Diligencias"
        titulo="Cargar formato existente"
        acciones={<BotonEnlace to="/diligencias">Volver</BotonEnlace>}
      />
      <p className="mb-4 text-[13px] text-texto-2">
        Para un formato que ya está aprobado en papel: sube su PDF (o el Word) y queda vigente con
        la revisión y la fecha del papel, sin pasar por las firmas de la app. Las hojas se llenan
        escribiendo con el lápiz encima de sus páginas.
      </p>
      <form onSubmit={(e) => void enviar(e)} noValidate>
        <Tarjeta titulo="El formato">
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              etiqueta="Código"
              ayuda="Como dice el papel, ej. CCI-REG-010"
              error={errores.codigo}
            >
              <input
                className={`${CLASE_ENTRADA} font-mono uppercase`}
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                placeholder="CCI-REG-010"
                autoComplete="off"
              />
            </Campo>
            <Campo etiqueta="Nombre" error={errores.nombre}>
              <input
                className={CLASE_ENTRADA}
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Verificación de básculas"
              />
            </Campo>
            <Campo etiqueta="Revisión" error={errores.revision}>
              <input
                className={CLASE_ENTRADA}
                type="number"
                min={1}
                max={99}
                value={revision}
                onChange={(e) => setRevision(e.target.value)}
              />
            </Campo>
            <Campo
              etiqueta="Fecha de aprobación"
              ayuda="La del encabezado del papel"
              error={errores.aprobadaEl}
            >
              <input
                className={CLASE_ENTRADA}
                type="date"
                value={aprobadaEl}
                onChange={(e) => setAprobadaEl(e.target.value)}
              />
            </Campo>
            <Campo etiqueta="Se conserva (meses)" ayuda="Retención de cada hoja llena">
              <input
                className={CLASE_ENTRADA}
                type="number"
                min={1}
                max={600}
                value={retencion}
                onChange={(e) => setRetencion(e.target.value)}
              />
            </Campo>
          </div>
          <div className="mt-4">
            <Dropzone archivo={archivo} onArchivo={setArchivo} error={errores.archivo} />
          </div>
          <label className="mt-4 flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={loLlenoYo}
              onChange={(e) => setLoLlenoYo(e.target.checked)}
              className="h-4 w-4"
            />
            Lo lleno yo (aparece en mis Diligencias)
          </label>
          {loLlenoYo && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-[13px]">
              <span className="text-texto-2">Me toca</span>
              <SelectorFrecuencia valor={frecuencia} onChange={setFrecuencia} />
            </div>
          )}
        </Tarjeta>
        {aviso && <Aviso className="mt-4">{aviso}</Aviso>}
        <div className="mt-4 flex justify-end">
          <Boton type="submit" disabled={ocupado}>
            {ocupado ? "Cargando…" : "Cargar formato"}
          </Boton>
        </div>
      </form>
    </div>
  );
}
